import { Wildfire, WildfireListItem } from 'features/wildfires/wildfire.types';
import { ENV } from 'shared/ENV';
import { LatLng, WithStringDates } from 'shared/types';
import { API, MapBoxAPI } from 'api';
import {
  Flooding,
  FloodingGeoEventData,
  FloodingListItem,
} from 'features/flooding/flooding.types';
import { WildfireGeoEventData } from 'features/wildfires/wildfireGeoEventData.types';
import {
  getOpacityForGeoEventMarker,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons/icons';
import { getFloodingEventIcon } from 'features/mapping/icons/iconData/floodingIcons';
import { ReactNode } from 'react';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { parseGeoEventDates } from 'features/geoEvents/geoEvent.utils';
import {
  GeocodeResult,
  GeoEventSearchResultDetails,
} from './useMapSearch.types';
import {
  getValidRegExp,
  parseDMS,
  parseLatitudeValue,
  parseLongitudeValue,
  removeAddressCountry,
} from '../../shared/utils';

const AUTOCOMPLETE_RADIUS = 500;

const GEO_EVENTS_FOR_SEARCH_LIMIT = 250;

type GoogleCoordsData = {
  coords: [lng: number, lat: number];
  formattedAddress: string;
};

/**
 * Takes an HTML string as input and checks if it contains any <b> tags.
 * If it doesn't, the function returns the original string as-is. However,
 * if <b> tags are present, the function extracts the content within the
 * first set of <b> tags and wraps it in a <b> JSX element. It then recursively
 * parses the text before and after that <b> tag, and finally, combines
 * all the parsed parts into a single JSX element.
 */
const parseHTML = (html: string): JSX.Element | string => {
  const regex = /<b>(.*?)<\/b>/g;
  const match = regex.exec(html);
  if (!match) {
    return html;
  }
  const [, innerHTML] = match;
  const beforeHTML = html.slice(0, match.index);
  const afterHTML = html.slice(match.index + match[0].length);
  return (
    <>
      {parseHTML(beforeHTML)}
      <b>{innerHTML}</b>
      {parseHTML(afterHTML)}
    </>
  );
};

export const getMapSearchResultName = (location: GeocodeResult): string =>
  `${location.address || ''} ${location.text}`.trim();

export const getHighlightedSearchMatch = (
  searchTerm: string,
  resultName: string,
): string | JSX.Element | JSX.Element[] => {
  const regex = getValidRegExp(`(${searchTerm.trim()})`, 'i');
  if (!regex) return resultName;
  const parsedResult = resultName.replace(regex, '<b>$1</b>');
  return parseHTML(parsedResult);
};

export const getGoogleCoords = async (
  placeId: string,
): Promise<GoogleCoordsData | null> => {
  if (!window.google) {
    return null;
  }

  const geocoder = new window.google.maps.Geocoder();
  const { results } = await geocoder.geocode({ placeId });

  const place = results[0];

  if (!place) {
    return null;
  }

  return {
    coords: [place.geometry.location.lng(), place.geometry.location.lat()],
    formattedAddress: removeAddressCountry(place.formatted_address),
  };
};

export const fetchGeoEventsForSearch = async (
  term: string,
  inactive: boolean = false,
): Promise<(Wildfire | Flooding)[]> => {
  const geoEventTypes = ['wildfire', 'flooding'];

  const params = {
    q: term,
    limit: GEO_EVENTS_FOR_SEARCH_LIMIT,
    reporterManagedOnly: false,
    geoEventTypes: geoEventTypes.join(','),
    ...(inactive ? { inactiveOnly: true } : {}),
  };

  const res = await API.get<{
    results: WithStringDates<Wildfire | Flooding>[];
  }>(`geo_events/search`, {
    params,
  });

  return res.data.results.map((geoEvent) => parseGeoEventDates(geoEvent));
};

export const fetchGoogleMapsSearch = async (
  query: string,
  proximity: string,
): Promise<GeocodeResult[]> => {
  if (!window.google) {
    return [];
  }
  const places = (await google.maps.importLibrary(
    'places',
  )) as google.maps.PlacesLibrary;
  const { Circle } = google.maps;

  const [lng, lat] = proximity.split(',').map((e) => parseFloat(e.trim()));

  const mapCircle = new Circle({
    center: new window.google.maps.LatLng(lat, lng),
    radius: AUTOCOMPLETE_RADIUS,
  });

  const googleMapsPlacesRequest: google.maps.places.AutocompleteRequest = {
    locationBias: mapCircle,
    input: query,
    region: 'us',
  };

  const { suggestions } =
    await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      googleMapsPlacesRequest,
    );

  // remove empty suggestions & transform
  // the remaining ones into GeocodeResults
  return suggestions.reduce<GeocodeResult[]>((geocodeResults, suggestion) => {
    const { placePrediction } = suggestion;

    if (!placePrediction) {
      return geocodeResults;
    }

    geocodeResults.push({
      id: placePrediction.placeId,
      type: 'Feature' as const,
      text: placePrediction.mainText?.text ?? '',
      placeName: placePrediction.secondaryText?.text ?? '',
      center: [], // Placeholder, this will be updated for the selected place
    });

    return geocodeResults;
  }, []);
};

export const fetchMapboxSearch = async (
  term: string,
  proximity: string,
): Promise<GeocodeResult[]> => {
  const res = await MapBoxAPI.get<{
    type: 'FeatureCollection';
    query: string[];
    features: GeocodeResult[];
  }>(`/${encodeURIComponent(term)}.json`, {
    params: {
      access_token: ENV.VITE_MAPBOX_ACCESS_TOKEN ?? '',
      proximity,
      types:
        'country,region,district,postcode,locality,place,address,poi,neighborhood',
      language: 'en',
      limit: '5',
      country: 'us',
    },
  });

  return res.data.features;
};

export const getLatLngFromString = (searchTerm: string): LatLng | null => {
  // Allow lat lng values separated by comma or whitespace.
  const rawValues = (
    searchTerm.includes(',')
      ? searchTerm.split(',')
      : searchTerm.trim().split(/\s+/)
  ).map((part) => part.trim());

  if (rawValues.length === 2) {
    const lat = parseLatitudeValue(rawValues[0]);
    const lng = parseLongitudeValue(rawValues[1]);

    if (lat && lng) {
      return { lat, lng: lng < 0 ? lng : lng * -1 };
    }
  }

  const { latitude, longitude } = parseDMS(searchTerm);

  if (latitude && longitude)
    return { lat: latitude, lng: longitude < 0 ? longitude : longitude * -1 };

  return null;
};

export const getGeoEventDetails = <
  T extends {
    isActive: boolean;
    dateModified: Date;
    geoEventType?: 'wildfire' | 'flooding';
    data: WildfireGeoEventData | FloodingGeoEventData;
  },
>(
  geoEvent: T,
): GeoEventSearchResultDetails<T> => {
  const base = {
    isActive: geoEvent.isActive,
    dateModified: geoEvent.dateModified,
    geoEventType: geoEvent.geoEventType ?? geoEvent.data.geoEventType,
    data: geoEvent.data,
  };

  if (geoEvent.data.geoEventType === 'flooding') {
    return base as GeoEventSearchResultDetails<T>;
  }

  return {
    ...base,
    containment: geoEvent.data.containment,
    acreage: geoEvent.data.acreage,
  } as GeoEventSearchResultDetails<T>;
};

// @todo remove when removing 'internal_tab_on_incidents' release flag
export const geoEventHasInternalNotes = (
  geoEvent: Wildfire | Flooding | WildfireListItem | FloodingListItem,
): boolean =>
  'reporterOnlyNotes' in geoEvent.data &&
  Boolean(geoEvent.data.reporterOnlyNotes);

export const geoEventHasReporterNotes = (
  geoEvent: Wildfire | Flooding | WildfireListItem | FloodingListItem,
): boolean => geoEvent.hasReporterNotes === true;

const GEO_EVENT_ICON_WIDTH_FUNCTION = {
  wildfire: (geoEvent: Partial<Wildfire>) =>
    geoEvent.data?.isComplexParent ? 40 : 24,
  flooding: () => 28,
};

export const getGeoEventAvatar = (
  geoEvent: Partial<Wildfire | Flooding> & {
    geoEventType: GeoEventType;
  },
  isHistorical = false,
): ReactNode => {
  const icon =
    geoEvent.geoEventType === 'wildfire'
      ? getWildfireGeoEventIcon(geoEvent as Partial<Wildfire>)
      : getFloodingEventIcon(geoEvent as Partial<Flooding>);
  const getWidth = GEO_EVENT_ICON_WIDTH_FUNCTION[geoEvent.geoEventType];
  if (!icon) return null;

  // @ts-expect-error geoEventType check above ensures correct type
  const width = getWidth?.(geoEvent);

  return (
    <img
      src={icon.data}
      alt=""
      width={width}
      height={40}
      style={{
        opacity: getOpacityForGeoEventMarker({
          geoEvent,
          isFadable: true,
          isHistorical,
        }),
      }}
    />
  );
};
